import { AppMessage } from '@/common/components';
import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { PageLayoutsFormFields, PageLayoutsFormInputs, PageLayoutsQueryKeys, useCreateLayoutsPageMutation } from '@/modules/Pages';

import { PageLayoutsFormView } from './PageLayoutsFormView';
import { pageLayoutsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
}

export const PageLayoutsCreateFormContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageLayoutsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageLayoutsFormInputs>>(null);

  const { mutate: createLayoutMutate, isLoading: isCreateLayoutLoading } = useCreateLayoutsPageMutation(siteId)({
    onSuccess: async ({ id }) => {
      appMessageApi.success('Макет успішно створено!');

      await queryClient.refetchQueries([PageLayoutsQueryKeys.GetAll, siteId], { exact: true });

      navigate(AppRoutes.PageLayoutsById.replace(':siteId', siteId).replace(':layoutId', id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageLayoutsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: PageLayoutsFormFields = {
    name: undefined,
    content: undefined
  };

  const handleFinish = useCallback((values: PageLayoutsFormFields): void => {
    setFormErrors(null);

    createLayoutMutate(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.SitesTemplates.replace(':siteId', siteId));
  }, [siteId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <PageLayoutsFormView
      form={ form }
      initialValues={ initialValues }
      formErrors={ formErrors }
      formName="page-layouts-create-form"
      formType="create"
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
      isFormLoading={ isCreateLayoutLoading }
      validationSchema={ pageLayoutsFormValidationSchema }
    />
  );
});
